import { useRef, useState } from "react";
import { FiEdit2, FiPlus } from "react-icons/fi";
import { uploadImage } from "api/upload";
import { getErrorMessage, imageUrl, isPreviewableImage } from "utils/cms";

export default function ImageField({
  label,
  value,
  onChange,
  id,
  compact = false,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const preview = imageUrl(value);
  const showImage = isPreviewableImage(value);

  const onFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!/\.(jpg|jpeg|png|gif|webp|mp4|mov|avi|mkv|webm)$/i.test(file.name)) {
      setError("Allowed: jpg, jpeg, png, gif, webp, mp4, mov, avi, mkv, webm");
      return;
    }
    if (file.size > 40 * 1024 * 1024) {
      setError("File must be 40MB or smaller");
      return;
    }

    setError("");
    setUploading(true);
    try {
      const res = await uploadImage(file);
      const imagePath = res.data?.data?.imagePath || res.data?.imagePath;
      if (!imagePath) {
        setError(res.data?.message || "Upload failed");
        return;
      }
      onChange(imagePath);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {label && !compact ? (
        <label
          htmlFor={id}
          className="text-xs font-semibold text-navy-700 dark:text-white"
        >
          {label}
        </label>
      ) : null}
      <div
        className={
          compact
            ? "group relative overflow-hidden rounded-lg"
            : "group relative mt-1 overflow-hidden rounded-lg border border-gray-200 dark:!border-white/10"
        }
      >
        <div
          className={
            compact
              ? "flex h-16 w-auto items-center justify-center"
              : "flex h-24 items-center justify-center bg-gray-50 p-2 dark:bg-white/5"
          }
        >
          {showImage ? (
            <img
              src={preview}
              alt={label}
              className="h-full w-auto max-w-full object-contain"
            />
          ) : value ? (
            <a
              href={preview}
              target="_blank"
              rel="noreferrer"
              className="truncate text-sm text-brand-500"
            >
              {value}
            </a>
          ) : (
            <p className="text-sm text-gray-600">No file uploaded</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          title={value ? "Replace image" : "Upload image"}
          className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 transition duration-200 group-hover:opacity-100 disabled:opacity-100"
        >
          <span
            className={`flex items-center justify-center rounded-full bg-white text-navy-700 shadow-lg ${
              compact ? "h-8 w-8" : "h-11 w-11"
            }`}
          >
            {value ? (
              <FiEdit2 className={compact ? "h-4 w-4" : "h-5 w-5"} />
            ) : (
              <FiPlus className={compact ? "h-4 w-4" : "h-5 w-5"} />
            )}
          </span>
        </button>
        <input
          id={id}
          ref={inputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.avi,.mkv,.webm"
          onChange={onFile}
          disabled={uploading}
          className="hidden"
        />
      </div>
      {uploading ? (
        <p className="mt-2 text-xs text-gray-600">Uploading…</p>
      ) : null}
      {error ? <p className="mt-2 text-xs text-red-500">{error}</p> : null}
    </div>
  );
}
